/* eslint-disable @typescript-eslint/no-explicit-any -- legacy untyped resource: responses are passed through as the server sends them. */
import { SdkContext } from '../internal'
import { withAnalyticsSession } from '@/lib/analytics/session'
import { listItems } from '@/lib/utils'
import type { ChangeTierQuote, ChangeTierRequest, ChangeTierResult, ScheduledChange } from '@/types/subscription'

export class SubscriptionsResource {
  constructor(private ctx: SdkContext) {}

  /** The caller's subscriptions; with `companyId` (F-93 1B), the company's (its admin only, 403 otherwise). */
  async list(params: { companyId?: string } = {}): Promise<any[]> {
    // A paginated listing: raw() normalizes it to { items, pagination }, which
    // the old `result.data` read as nothing, so /billing never found a subscription.
    const query = params.companyId ? { companyId: params.companyId } : undefined
    const result = await this.ctx.raw<any>({ httpMethod: 'GET', path: '/subscriptions' }, query ? { query } : {})
    return listItems(result)
  }

  /** F-93 1B: the company's purchases (`GET /purchases/me?companyId=`), newest first. Its admin only. */
  async companyPurchases(companyId: string, params: { page?: number; limit?: number } = {}): Promise<any[]> {
    const result = await this.ctx.raw<any>({ httpMethod: 'GET', path: '/purchases/me' }, { query: { companyId, ...params } })
    return listItems(result)
  }

  async get(id: string): Promise<any> {
    return this.ctx.raw({ httpMethod: 'GET', path: `/subscriptions/{id}` }, { id })
  }

  async create(subscription: any): Promise<any> {
    return this.ctx.raw(
      { httpMethod: 'POST', path: '/subscriptions', hasBody: true },
      { body: subscription }
    )
  }

  async update(id: string, subscription: any): Promise<any> {
    return this.ctx.raw(
      { httpMethod: 'PUT', path: `/subscriptions/{id}`, hasBody: true },
      { id, body: subscription }
    )
  }

  async getBillings(
    subscriptionId: string,
    params?: { page?: number; pageSize?: number }
  ): Promise<any> {
    return this.ctx.raw(
      { httpMethod: 'GET', path: `/subscriptions/{id}/billings` },
      { id: subscriptionId, query: params }
    )
  }

  /** FIN-68: what changing to another option would do (mode, charge now, next charge). No effect. */
  async quoteChangeTier(subscriptionId: string, request: ChangeTierRequest): Promise<ChangeTierQuote> {
    return this.ctx.raw<ChangeTierQuote>(
      { httpMethod: 'POST', path: `/subscriptions/{id}/change-tier/quote`, hasBody: true },
      { id: subscriptionId, body: request }
    )
  }

  /** Upgrade now with proration (`immediate: true`) or schedule the change; the server decides which. */
  async changeTier(subscriptionId: string, request: ChangeTierRequest): Promise<ChangeTierResult> {
    return this.ctx.raw<ChangeTierResult>(
      { httpMethod: 'POST', path: `/subscriptions/{id}/change-tier`, hasBody: true },
      { id: subscriptionId, body: withAnalyticsSession(request) }
    )
  }

  async cancel(subscriptionId: string, request: any = {}): Promise<any> {
    return this.ctx.raw(
      { httpMethod: 'POST', path: `/subscriptions/{id}/cancel`, hasBody: true },
      { id: subscriptionId, body: request }
    )
  }

  /** F-37: what adding an add-on to a running plan costs now and from the next charge. No effect. */
  async quoteAddOn(subscriptionId: string, productId: string): Promise<any> {
    return this.ctx.raw(
      { httpMethod: 'POST', path: `/subscriptions/{id}/add-ons/quote`, hasBody: true },
      { id: subscriptionId, body: { productId } }
    )
  }

  /** F-37: adds the add-on to the plan, charging the prorated amount (201). */
  async addAddOn(
    subscriptionId: string,
    request: {
      productId: string
      paymentMethod?: string
      installments?: number
      paymentMethodId?: string
      cardToken?: string
      customerData?: Record<string, unknown>
    }
  ): Promise<any> {
    return this.ctx.raw(
      { httpMethod: 'POST', path: `/subscriptions/{id}/add-ons`, hasBody: true },
      { id: subscriptionId, body: withAnalyticsSession(request) }
    )
  }

  /** F-38: pays a past-due plan now (plan and the add-ons billed with it), in one charge (201). */
  async payNow(
    subscriptionId: string,
    request: {
      paymentMethod: string
      installments?: number
      paymentMethodId?: string
      cardToken?: string
      customerData?: Record<string, unknown>
    }
  ): Promise<any> {
    return this.ctx.raw(
      { httpMethod: 'POST', path: `/subscriptions/{id}/pay-now`, hasBody: true },
      { id: subscriptionId, body: request }
    )
  }

  async reactivate(subscriptionId: string): Promise<any> {
    return this.ctx.raw(
      { httpMethod: 'POST', path: `/subscriptions/{id}/reactivate`, hasBody: true },
      { id: subscriptionId, body: {} }
    )
  }

  /** The change waiting for the end of the period; null on the 404 the API gives when there is none. */
  async getScheduledChange(subscriptionId: string): Promise<ScheduledChange | null> {
    try {
      return await this.ctx.raw<ScheduledChange>(
        { httpMethod: 'GET', path: `/subscriptions/{id}/scheduled-change` },
        { id: subscriptionId }
      )
    } catch (error: any) {
      if (error?.response?.status === 404 || error?.status === 404) {
        return null
      }
      throw error
    }
  }

  async cancelScheduledChange(subscriptionId: string): Promise<void> {
    await this.ctx.raw(
      { httpMethod: 'DELETE', path: `/subscriptions/{id}/scheduled-change` },
      { id: subscriptionId }
    )
  }

  async delete(id: string): Promise<any> {
    return this.cancel(id, { immediate: false })
  }
}
